import Loader from '@components/Loader'
import { MeFavoritesProductsQueryResult } from '@graphql/queries/__generated__/me.generated'
import { Divider, IconButton } from '@mui/material'
import Pagination from '@mui/material/Pagination'
import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { getProductUrl } from '../../../urls'
import FavoritesLinePrice from './FavoritesLinePrice'
import { useTranslation } from 'react-i18next'
import DeleteIcon from '@icons/DeleteIcon'
import { scrollToBlockTop } from '@components/Header/HeaderBase'

interface CartFavoritesProps {
  favoritesQueryResult: MeFavoritesProductsQueryResult
  favoritesLineLoading: number | null

  onClickProduct(specId: string | number, slug: string): void

  handleRemoveFromFavorites(productPk: number): Promise<void>

  page: number

  setPage(page: number): void
}

const CartFavorites: React.FC<CartFavoritesProps> = ({
  favoritesQueryResult,
  favoritesLineLoading,
  onClickProduct,
  handleRemoveFromFavorites,
  page,
  setPage,
}) => {
  const { t } = useTranslation()
  const blockRef = useRef<HTMLDivElement>(null)
  const [products, setProducts] = useState<
    MeFavoritesProductsQueryResult['data']['me']['wishedProducts']['results'][0][]
  >(favoritesQueryResult.data?.me?.wishedProducts.results || [])
  useEffect(() => {
    if (!favoritesQueryResult.loading) {
      setProducts(favoritesQueryResult.data?.me?.wishedProducts.results || [])
    }
  }, [favoritesQueryResult.data, favoritesQueryResult.loading])
  const handleClickProduct = (specId: number, slug: string) => e => {
    e.preventDefault()
    onClickProduct(specId, slug)
  }
  const totalCount = favoritesQueryResult.data?.me?.wishedProducts.totalCount || 0
  const limit = favoritesQueryResult.data?.me?.wishedProducts.pageInfo?.limit || 1
  const numPages = Math.ceil(totalCount / limit)
  return (
    <div ref={blockRef} className='relative bg-white shadow-card rounded-lg'>
      <div className='flex flex-col md:flex-row md:items-center flex-wrap p-20 py-[18px]'>
        <h1 className='font-bold text-lg uppercase mr-10 leading-24'>{t('title.savedForLater', 'Saved for later')}</h1>
        <span className='text-13 text-gray-700 leading-18'>
          {t('itemsWithCount', { defaultValue: '{{count}} Items', count: totalCount })}
        </span>
      </div>
      <Divider />
      {!products.length && (
        <div className='py-40 text-center text-lg relative'>
          <Loader
            className='spinner spinner--small'
            type='ThreeDots'
            color='#333'
            visible={favoritesQueryResult.loading}
            height={100}
            width={100}
          />
          {!favoritesQueryResult.loading && (
            <span>{t('yourSavedForLaterIsEmpty', 'Your Saved for Later is empty.')}</span>
          )}
        </div>
      )}
      {!!products.length && (
        <Loader
          className='spinner spinner--small'
          type='ThreeDots'
          color='#333'
          visible={favoritesQueryResult.loading}
          height={100}
          width={100}
        />
      )}
      <ul className='p-0 list-none'>
        {products.map((p, idx) => {
          const spec = p.specifics[0]
          return (
            <li key={p.id} className='relative'>
              <Loader
                className='spinner spinner--small'
                type='ThreeDots'
                color='#333'
                visible={favoritesLineLoading === p.pk}
                height={100}
                width={100}
              />
              <div className='p-20 flex'>
                <Link
                  to={getProductUrl(spec?.slug, spec?.pk)}
                  onClick={handleClickProduct(spec?.pk, spec?.slug)}
                  className='mr-20 w-80 min-w-80 sm:w-120 sm:min-w-120'
                >
                  <img className='max-w-full shadow-thumbnail rounded-lg' src={p.thumbnail.md} alt={spec?.name} />
                </Link>
                <div className='flex-1'>
                  <div className='flex text-13 leading-18'>
                    <div className='w-full'>
                      <Link
                        to={getProductUrl(spec?.slug, spec?.pk)}
                        onClick={handleClickProduct(spec?.pk, spec?.slug)}
                        className='no-underline break-words text-black text-lg'
                      >
                        {spec?.name}
                      </Link>
                      {p.sku && (
                        <div className='mt-10 break-all line-clamp-1 text-gray-700 uppercase'>
                          <span className='font-bold'>{t('sku', 'SKU')}: </span>
                          <span title={p.sku}>{p.sku}</span>
                        </div>
                      )}
                    </div>
                    <div className='ml-12 w-auto sm:w-250 text-right flex flex-col mt-4'>
                      <FavoritesLinePrice priceRange={p.priceRange} qty={1} />
                    </div>
                  </div>
                  <div className='flex items-center justify-end mt-15'>
                    <IconButton
                      className='flex-shrink-0 border border-solid border-gray-400 rounded-lg'
                      onClick={() => handleRemoveFromFavorites(p.pk)}
                      title={t('button.remove', 'Remove')}
                    >
                      <DeleteIcon viewBox='0 -960 960 960' className='text-red' fontSize='small' />
                    </IconButton>
                  </div>
                </div>
              </div>
              {idx !== products.length - 1 && <Divider sx={{ margin: '10px 20px', borderColor: '#d8d8d8' }} />}
            </li>
          )
        })}
      </ul>
      <div className='flex items-center justify-center pb-20'>
        {numPages > 1 && (
          <Pagination
            count={numPages}
            page={page || 1}
            size='small'
            onChange={(e, page) => {
              setPage(page)
              scrollToBlockTop(blockRef.current)
            }}
            siblingCount={1}
            shape='rounded'
          />
        )}
      </div>
    </div>
  )
}

export default CartFavorites
